import { useEffect, useMemo } from 'react';
import {
  playbackMs as clampPlaybackMs, PLAYBACK_MS_MAX, PLAYBACK_MS_MIN, ROBOT_COLOURS,
} from '@roborally/engine';
import type { CourseFeatures } from '@roborally/engine';
import type { OptionCardView } from '../api.js';
import { isClassic, isMulticard, modeCards as cardsOf, TURN_TIMER_UNIT_SECONDS } from '../gameSetup.js';
import type { GameSetup, TurnTimerUnit } from '../gameSetup.js';
import { optionTooltip } from '../optionPayload.js';
import { ColourPicker } from './ColourPicker.js';

/**
 * Every setting of a game but its course, as the New Game screen asks
 * them. Shared with the saved game setups, so a setup is edited with the
 * same questions a game is created with.
 *
 * Rendered as the children of a `.settings` grid, which the caller owns:
 * New Game puts the course name in front of them.
 */
export function GameSetupFields({ value, onChange, catalog, course }: {
  value: GameSetup;
  onChange: (next: GameSetup) => void;
  /** Every Option card, alphabetical. */
  catalog: OptionCardView[];
  /** The course's features, where there is a course: its cards only. */
  course?: CourseFeatures;
}) {
  const {
    colour, maxPlayers, livesOn, lifeTokens, turnMode, optionsEnabled, chosenOptions,
    startingOptions, debrisEnabled, lateJoin, botsAllowed, timerOn, timerSeconds,
    turnTimerAmount, turnTimerUnit, timerMode, playbackMs,
  } = value;
  const set = (patch: Partial<GameSetup>) => onChange({ ...value, ...patch });
  const setColour = (v: string | null) => set({ colour: v });
  const setMaxPlayers = (v: number) => set({ maxPlayers: v });
  const setLivesOn = (v: boolean) => set({ livesOn: v });
  const setLifeTokens = (v: number) => set({ lifeTokens: v });
  const setOptionsEnabled = (v: boolean) => set({ optionsEnabled: v });
  const setChosenOptions = (v: string[] | null) => set({ chosenOptions: v });
  const setStartingOptions = (v: number) => set({ startingOptions: v });
  const setDebrisEnabled = (v: boolean) => set({ debrisEnabled: v });
  const setLateJoin = (v: boolean) => set({ lateJoin: v });
  const setBotsAllowed = (v: boolean) => set({ botsAllowed: v });
  const setTimerOn = (v: boolean) => set({ timerOn: v });
  const setTimerSeconds = (v: number) => set({ timerSeconds: v });
  const setTurnTimerAmount = (v: number) => set({ turnTimerAmount: v });
  const setTurnTimerUnit = (v: TurnTimerUnit) => set({ turnTimerUnit: v });

  const modeCards = useMemo(
    () => cardsOf(catalog, turnMode, debrisEnabled, course),
    [catalog, turnMode, debrisEnabled, course],
  );
  // Turn mode has fewer cards, so a count chosen in normal mode can be
  // more than it holds, which the server refuses.
  useEffect(() => {
    if (modeCards.length > 0 && startingOptions > modeCards.length) {
      setStartingOptions(modeCards.length);
    }
  });

  return (
    <>
      <div>
        <label>Choose your color</label>
        <ColourPicker colours={[...ROBOT_COLOURS]} value={colour} onChange={setColour} />
      </div>

      <div>
        <label htmlFor="players">Maximum players</label>
        <input
          id="players"
          type="number"
          min={1}
          max={8}
          value={maxPlayers}
          onChange={(e) => setMaxPlayers(Number(e.target.value))}
        />
      </div>

      <div>
        <label>Game mode</label>
        <div className="choice-box">
          <label
            className="powerdown"
            title="The game as printed: players can be interrupted mid-turn for decisions."
          >
            <input
              type="radio"
              name="game-mode"
              checked={!turnMode}
              onChange={() => set({ turnMode: false, chosenOptions: null })}
            />
            <span>Normal</span>
          </label>
          <label
            className="powerdown"
            title={'Every decision is made while programming; the turn runs straight through and is '
              + 'played back. Decisions that would stop a register are pre-set, determined while '
              + 'programming, or left out.'}
          >
            <input
              type="radio"
              name="game-mode"
              checked={turnMode}
              onChange={() => set({ turnMode: true, chosenOptions: null })}
            />
            <span>Turn</span>
          </label>
        </div>
      </div>

      <div>
        <label>Programming timer</label>
        {/* One choice of three, the duration under it: off, a clock for
            the whole table, or one that starts on the last player left. */}
        <div className="choice-box">
          <label className="powerdown" title="Programming waits as long as it takes.">
            <input
              type="radio"
              name="timer-mode"
              checked={!timerOn}
              onChange={() => setTimerOn(false)}
            />
            <span>None</span>
          </label>
          <label
            className="powerdown"
            title={'Everyone has this long to program the turn. Not used on Turn 1.'}
          >
            <input
              type="radio"
              name="timer-mode"
              checked={timerOn && timerMode === 'hand'}
              onChange={() => set({ timerOn: true, timerMode: 'hand' })}
            />
            <span>Each turn</span>
          </label>
          <label
            className="powerdown"
            title={'The clock starts when only one player is still programming. '
              + 'In a game of four, the third player to finish starts the fourth player\'s clock; '
              + 'in a game of two, the first to finish starts the other\'s. Not used on Turn 1.'}
          >
            <input
              type="radio"
              name="timer-mode"
              checked={timerOn && timerMode === 'last'}
              onChange={() => set({ timerOn: true, timerMode: 'last' })}
            />
            <span>Next-to-last finished</span>
          </label>
          {/* The box is a bare number, so the unit has to be stated or 120
              reads as easily as minutes as seconds. Turn mode's turns can be
              taken hours apart, so its clock is set in seconds, minutes or
              hours, from 30 seconds to a week. */}
          {timerOn && (turnMode ? (
            <div className="row" style={{ gap: '0.4rem' }}>
              <input
                id="timer-amount"
                aria-label="Duration"
                type="number"
                min={turnTimerUnit === 'seconds' ? 30 : 1}
                max={7 * 24 * 3600 / TURN_TIMER_UNIT_SECONDS[turnTimerUnit]}
                value={turnTimerAmount}
                onChange={(e) => setTurnTimerAmount(Number(e.target.value))}
              />
              <select
                aria-label="unit"
                value={turnTimerUnit}
                onChange={(e) => setTurnTimerUnit(e.target.value as TurnTimerUnit)}
              >
                <option value="seconds">seconds</option>
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
              </select>
            </div>
          ) : (
            <div className="row" style={{ gap: '0.4rem' }}>
              <input
                id="timer-seconds"
                aria-label="Duration in seconds"
                type="number"
                min={30}
                max={600}
                step={5}
                value={timerSeconds}
                onChange={(e) => setTimerSeconds(Number(e.target.value))}
              />
              <span className="muted">seconds</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="playback-ms">Playback step</label>
        <div className="row" style={{ gap: '0.4rem' }}>
          <input
            id="playback-ms"
            type="number"
            title={'Milliseconds between steps when a register plays back: live, in turn mode, '
              + `and replayed from the log. ${PLAYBACK_MS_MIN} to ${PLAYBACK_MS_MAX}.`}
            min={PLAYBACK_MS_MIN}
            max={PLAYBACK_MS_MAX}
            step={50}
            value={playbackMs}
            onChange={(e) => set({ playbackMs: Number(e.target.value) })}
            // Held to the range once the host is done typing, so a value
            // part way through being typed is not snapped.
            onBlur={() => set({ playbackMs: clampPlaybackMs(playbackMs) })}
          />
          <span className="muted">ms</span>
        </div>
      </div>

      {/* The rules that are on or off, as one list: each says what it is,
          and its tooltip what it does. */}
      <div>
        {/* Blank, so the box lines up with the headed ones beside it. */}
        <label aria-hidden="true">&nbsp;</label>
        <div className="choice-box">
          <div className="row" style={{ gap: '0.5rem' }}>
            <label
              className="powerdown"
              title="Off means no robot elimination; deaths are still tracked."
            >
              <input
                type="checkbox"
                checked={livesOn}
                onChange={(e) => setLivesOn(e.target.checked)}
              />
              <span>Use life tokens</span>
            </label>
            {livesOn && (
              <input
                type="number"
                aria-label="Life tokens"
                min={1}
                max={9}
                value={lifeTokens}
                onChange={(e) => setLifeTokens(Number(e.target.value))}
                style={{ width: '4rem' }}
              />
            )}
          </div>
          {/* The rule VERBATIM.
              Not to be reworded or trimmed to fit: a host reading a
              paraphrase has to guess at the thresholds, and the two that
              matter here -- 2 or more damage, and enters AND exits within
              one register phase -- are exactly what a summary drops. */}
          <label
            className="powerdown"
            title={'Whenever a robot is destroyed or receives 2 or more points of damage during a '
              + 'register phase, a debris token will appear in that robot\'s square. A robot that '
              + 'enters and exits a square with debris during a single register phase receives a '
              + 'point of damage. Movement includes being pushed or sliding. Debris is removed '
              + 'immediately after causing damage.'}
          >
            <input
              type="checkbox"
              checked={debrisEnabled}
              onChange={(e) => setDebrisEnabled(e.target.checked)}
            />
            <span>Use debris</span>
          </label>
          <label
            className="powerdown"
            title={'A latecomer comes in on the last flag every player has tagged, or on the start '
              + 'track if anyone has yet to reach flag 1. When disabled no one can join the game '
              + 'after it starts.'}
          >
            <input
              type="checkbox"
              checked={lateJoin}
              onChange={(e) => setLateJoin(e.target.checked)}
            />
            <span>Let players join after the game starts</span>
          </label>
          {turnMode && (
            <label
              className="powerdown"
              title={'A computer player the host runs using the bot token in their account panel. '
                + 'When disabled no bot can take a seat.'}
            >
              <input
                type="checkbox"
                checked={botsAllowed}
                onChange={(e) => setBotsAllowed(e.target.checked)}
              />
              <span>Let bots join</span>
            </label>
          )}
        </div>
      </div>

      <div>
        <label>Option cards</label>
        <div className="choice-box">
          <div className="row" style={{ gap: '0.5rem' }}>
            <label className="powerdown">
              <input
                type="checkbox"
                checked={optionsEnabled}
                onChange={(e) => setOptionsEnabled(e.target.checked)}
              />
              <span>Use Option cards</span>
            </label>
            {optionsEnabled && (
              <>
                <label htmlFor="starting-options" className="choice-text"
                  title="Number of options dealt to every robot when the game starts.">
                  Start with:
                </label>
                <input
                  id="starting-options"
                  type="number"
                  title="Number of options dealt to every robot when the game starts."
                  min={0}
                  // The mode's cards, which is the most a robot could be dealt.
                  // Undefined until it has loaded: the server has the real
                  // ceiling, and a max of 0 in the meantime would clamp
                  // whatever the host typed down to nothing.
                  max={modeCards.length || undefined}
                  value={startingOptions}
                  onChange={(e) => setStartingOptions(Math.max(0, Math.min(
                    modeCards.length || Number.MAX_SAFE_INTEGER,
                    Number(e.target.value) || 0,
                  )))}
                  style={{ width: '4rem' }}
                />
              </>
            )}
          </div>
        </div>

        {/* Which cards are in the deck. All of them unless the host
            says otherwise, so a host who does not care need not
            read the list. */}
        {optionsEnabled && modeCards.length > 0 && (
          <div className="option-picker">
            <div className="spread" style={{ marginBottom: '0.35rem' }}>
              <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                {chosenOptions
                  ? modeCards.filter((o) => chosenOptions.includes(o.id)).length
                  : modeCards.length} of {modeCards.length} in the deck
              </span>
              <div className="row" style={{ gap: '0.3rem' }}>
                <button
                  className="secondary tiny"
                  onClick={() => setChosenOptions(null)}
                >
                  All
                </button>
                {/* Turn mode's cards never interrupt, so there is
                    nothing for this to take out. */}
                {!turnMode && (
                  <button
                    className="secondary tiny"
                    title="Only the options that never pause a register to ask a question"
                    onClick={() => setChosenOptions(
                      modeCards.filter((o) => !o.interrupts).map((o) => o.id),
                    )}
                  >
                    Non-Interrupt
                  </button>
                )}
                <button
                  className="secondary tiny"
                  onClick={() => setChosenOptions([])}
                >
                  None
                </button>
                {/* The two-card Options and the side manoeuvres, in or
                    out together. On while any of them is in. */}
                {(() => {
                  const current = chosenOptions ?? modeCards.map((o) => o.id);
                  const multi = modeCards.filter(isMulticard).map((o) => o.id);
                  const on = multi.some((id) => current.includes(id));
                  return (
                    <button
                      className={on ? 'tiny' : 'secondary tiny'}
                      title={on
                        ? 'Take out the multicard options: two cards in a register, and the side manoeuvres'
                        : 'Put in the multicard options: two cards in a register, and the side manoeuvres'}
                      onClick={() => setChosenOptions(on
                        ? current.filter((id) => !multi.includes(id))
                        : [...current.filter((id) => !multi.includes(id)), ...multi])}
                    >
                      Multicard
                    </button>
                  );
                })()}
                {/* The original game's Options, in or out together. On
                    while any of them is in. */}
                {(() => {
                  const current = chosenOptions ?? modeCards.map((o) => o.id);
                  const classic = modeCards.filter(isClassic).map((o) => o.id);
                  const on = classic.some((id) => current.includes(id));
                  return (
                    <button
                      className={on ? 'tiny' : 'secondary tiny'}
                      title={on
                        ? 'Take out the classic options: the original game\'s, not Off the Grid\'s'
                        : 'Put in the classic options: the original game\'s, not Off the Grid\'s'}
                      onClick={() => setChosenOptions(on
                        ? current.filter((id) => !classic.includes(id))
                        : [...current.filter((id) => !classic.includes(id)), ...classic])}
                    >
                      Classic
                    </button>
                  );
                })()}
              </div>
            </div>

            {modeCards.map((option) => {
              const on = (chosenOptions ?? modeCards.map((o) => o.id)).includes(option.id);
              return (
                <label key={option.id} className="option-pick" title={optionTooltip(option)}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => {
                      const current = chosenOptions ?? modeCards.map((o) => o.id);
                      setChosenOptions(on
                        ? current.filter((id) => id !== option.id)
                        : [...current, option.id]);
                    }}
                  />
                  <span>{option.name}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
